import { type PresenceResult, createPresence } from "@solid-primitives/presence";
import { type AnimationParams, animate, stagger } from "animejs";
import { type Accessor, type Component, type JSX, Show, createEffect, createSignal, on, onCleanup } from "solid-js";

export type AnimationProps = {
  ref: (el: HTMLElement) => void;
  isMounted: Accessor<boolean>;
  isVisible: Accessor<boolean>;
  totalDuration: Accessor<number>;
};

export const AnimateChildren: Component<{
  show: boolean;
  children: (animationProps: AnimationProps) => JSX.Element;
  /**
   * Time in milliseconds to delay the animation
   * @default 0
   */
  delay?: number;
  /**
   * Time in milliseconds for the entire animation
   * @default 200
   */
  duration?: number;
  enter: AnimationParams;
  exit: AnimationParams;

  presence?: PresenceResult<boolean>;
}> = (props) => {
  const duration = () => props.duration ?? 200;
  const delay = () => props.delay ?? 0;

  const totalDuration = () => duration() + delay();

  const [refs, setRefs] = createSignal<HTMLElement[]>([]);

  const registerRef = (el: HTMLElement) => {
    setRefs((prev) => [...prev, el]);
  };

  const { isVisible, isMounted } =
    props.presence ??
    createPresence(() => props.show, {
      transitionDuration: totalDuration,
    });

  createEffect(() => {
    if (!isMounted()) {
      setRefs([]);
      return;
    }
  });

  createEffect(
    on(isVisible, (visible) => {
      const els = refs();
      if (els.length === 0) return;

      const animation = animate(els, {
        delay: delay(),
        duration: duration(),
        ...(visible ? props.enter : props.exit),
      });
      onCleanup(() => animation.cancel());
    }),
  );

  return (
    <Show when={isMounted()}>
      {props.children({ ref: registerRef, isMounted, isVisible, totalDuration: totalDuration })}
    </Show>
  );
};
